"use client";

import Tooltip from "@mui/material/Tooltip";
import { ComponentProps, useState } from "react";
import { twMerge } from "tailwind-merge";

export type ButtonGroupOption = {
  id: string;
  name: string;
  tooltip?: string;
};

export type ButtonGroupProps = ComponentProps<"nav"> & {
  options: ButtonGroupOption[];
  activeId?: string;
  onButtonClick?: (value: string) => void;
};

export const ButtonGroup = ({
  options,
  activeId,
  onButtonClick,
  ...props
}: ButtonGroupProps) => {
  if (options.length === 0) {
    return null;
  }

  const [active, setActive] = useState(activeId ?? options[0].id);

  const onClick = (value: string) => {
    setActive(value);
    onButtonClick?.(value);
  };

  return (
    <nav
      {...props}
      className={twMerge(
        "isolate grid h-10 divide-x divide-gray-200 rounded-lg border",
        props.className
      )}
      style={{
        gridTemplateColumns: `repeat(${options.length},minmax(0, 1fr))`,
        ...props.style,
      }}
    >
      {options.map((option, i) => {
        const isActive = option.id === active;
        return (
          <Tooltip
            key={option.id}
            arrow={true}
            title={option.tooltip}
            placement="top"
            slotProps={{
              tooltip: { className: "bg-zinc-900 text-white" },
              arrow: { className: "text-zinc-900" },
            }}
          >
            <button
              className={twMerge(
                "relative min-w-0 overflow-hidden whitespace-nowrap bg-white px-4 text-center text-sm font-medium focus:z-10",
                isActive
                  ? "bg-sky-100 font-bold text-sky-700"
                  : "text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700",
                i === 0 ? "rounded-l-lg" : "",
                i === options.length - 1 ? "rounded-r-lg" : ""
              )}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onClick(option.id)}
            >
              <span>{option.name}</span>
            </button>
          </Tooltip>
        );
      })}
    </nav>
  );
};
